import React, { useRef, useEffect, useCallback } from 'react';
import { TickerRow } from './TickerRow';
import { TileMessage } from './Tile';

export interface TickerUser {
  id: string;
  name: string;
  color: string;
  buffer: string;
  messages: TileMessage[];
  lastActivity: number | null;
  velocity?: number;
}

interface DepartingUser extends TickerUser {
  departedAt: number;
}

interface TickerListProps {
  users: TickerUser[];
  now: number;
  textScale?: number;
  localId?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  onInput?: (text: string) => void;
  onSubmit?: () => void;
  onBadgeTap?: () => void;
  isMobile?: boolean;
}

export function TickerList({ users, now, textScale = 1, localId, inputRef, onInput, onSubmit, onBadgeTap, isMobile = true }: TickerListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const prevActiveSpeakers = useRef<Set<string>>(new Set());

  // --- Arrival / departure tracking (via refs for synchronous render) ---
  const prevUserIdsRef = useRef<Set<string>>(new Set());
  const prevUsersSnap = useRef<Map<string, TickerUser>>(new Map());
  const arrivingRef = useRef<Map<string, number>>(new Map());       // id → timestamp
  const departingRef = useRef<Map<string, DepartingUser>>(new Map()); // id → snapshot

  const currentIds = new Set(users.map(u => u.id));
  const nowMs = Date.now();

  // Detect new arrivals (skip local user)
  for (const id of currentIds) {
    // If a departing user rejoins, cancel the departure
    if (departingRef.current.has(id)) {
      departingRef.current.delete(id);
    }
    if (!prevUserIdsRef.current.has(id) && id !== localId) {
      arrivingRef.current.set(id, nowMs);
    }
  }

  // Detect departures (skip local user)
  for (const id of prevUserIdsRef.current) {
    if (!currentIds.has(id) && id !== localId) {
      const snap = prevUsersSnap.current.get(id);
      if (snap) {
        departingRef.current.set(id, { ...snap, departedAt: nowMs });
      }
    }
  }

  // Prune expired entries
  for (const [id, ts] of arrivingRef.current) {
    if (nowMs - ts > 500) arrivingRef.current.delete(id);
  }
  for (const [id, data] of departingRef.current) {
    if (nowMs - data.departedAt > 800) departingRef.current.delete(id);
  }

  // Update snapshots for next render
  prevUserIdsRef.current = currentIds;
  const nextSnap = new Map<string, TickerUser>();
  users.forEach(u => nextSnap.set(u.id, u));
  prevUsersSnap.current = nextSnap;

  // --- Refs + scroll logic ---
  const setRowRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);

  useEffect(() => {
    const remoteUsers = users.filter(u => u.id !== localId);
    const currentActive = new Set(remoteUsers.filter(u => u.buffer.length > 0).map(u => u.id));
    const container = listRef.current;
    if (!container) {
      prevActiveSpeakers.current = currentActive;
      return;
    }

    for (const id of currentActive) {
      if (!prevActiveSpeakers.current.has(id)) {
        const row = rowRefs.current.get(id);
        if (row) {
          const containerRect = container.getBoundingClientRect();
          const rowRect = row.getBoundingClientRect();
          const isVisible = rowRect.top >= containerRect.top && rowRect.bottom <= containerRect.bottom;
          if (!isVisible) {
            row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }
    }

    prevActiveSpeakers.current = currentActive;
  });

  // --- Render helpers ---
  const renderRow = (u: TickerUser, isLocal: boolean, isDeparting: boolean) => {
    const isArriving = arrivingRef.current.has(u.id);
    const anim = isArriving
      ? 'fadeIn 400ms ease-out'
      : isDeparting
        ? 'fadeOut 800ms ease-out forwards'
        : undefined;

    const wrapper = anim || isDeparting;

    const row = (
      <TickerRow
        ref={(el) => setRowRef(u.id, el)}
        name={u.name}
        color={u.color}
        buffer={isDeparting ? '' : u.buffer}
        messages={u.messages}
        lastActivity={u.lastActivity}
        now={now}
        textScale={textScale}
        velocity={u.velocity}
        maxLines={isMobile ? 1 : (isLocal ? 3 : 3)}
        isLocal={isLocal}
        inputRef={isLocal ? inputRef : undefined}
        onInput={isLocal ? onInput : undefined}
        onSubmit={isLocal ? onSubmit : undefined}
        onBadgeTap={isLocal ? onBadgeTap : undefined}
      />
    );

    if (wrapper) {
      return (
        <div
          key={u.id}
          style={{
            animation: anim,
            pointerEvents: isDeparting ? 'none' : undefined,
          }}
        >
          {row}
        </div>
      );
    }

    return React.cloneElement(row, { key: u.id });
  };

  // Build departing-user list (only those not already in active users)
  const activeDeparting = Array.from(departingRef.current.values())
    .filter(d => !currentIds.has(d.id));

  // Both mobile and desktop: local user pinned at bottom, remote users scroll above
  const localUser = users.find(u => u.id === localId);
  const remoteUsers = users.filter(u => u.id !== localId);

  if (isMobile) {
    return (
      <div className="pulse-ticker-wrapper">
        <div ref={listRef} className="pulse-ticker-list">
          {remoteUsers.map(u => renderRow(u, false, false))}
          {activeDeparting.map(u => renderRow(u, false, true))}
        </div>
        {localUser && renderRow(localUser, true, false)}
      </div>
    );
  }

  return (
    <div className="pulse-ticker-wrapper">
      <div ref={listRef} className="pulse-ticker-desktop">
        {remoteUsers.map(u => renderRow(u, false, false))}
        {activeDeparting.map(u => renderRow(u, false, true))}
      </div>
      {localUser && renderRow(localUser, true, false)}
    </div>
  );
}
